import { useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import AssignLearnerIcon from '@mui/icons-material/School';
import AssumeUserIcon from '@mui/icons-material/SwitchAccount';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteUserIcon from '@mui/icons-material/PersonRemove';
import EmailIcon from '@mui/icons-material/Email';
import dayjs from 'dayjs';
import { PathDataGrid } from '~common/components';
import PageTitle from '../../components/pageTitle';

import AssignLearnersDialog from './assignLearnersDialog';
import DeleteUserDialog from './deleteUserDialog';
import UpdateEmailDialog from './updateEmailDialog';
import useUsers from './hooks/useUsers';
import useStyles from './styles';
import columns from './columns';
// eslint-disable-next-line import/extensions
import ResetPasswordDialog from './resetPasswordDialog/index.jsx';

function Users() {
  const { classes } = useStyles();
  const {
    data,
    loading,
    alert,
    selectedUser,
    selectionModel,
    onSelectionModelChange,
    onAssumeUserClick,
    onRefreshClick,
    showAssignLearnersDailog,
    openAssignLearnersDialog,
    dismissAssignLearnersDialog,
    submitAssignLearnersDialog,
    showPasswordResetDialog,
    openResetPasswordDialog,
    dismissPasswordResetDialog,
    submitPasswordResetDialog,
    showDeleteUserDialog,
    openDeleteUserDialog,
    dismissDeleteUserDialog,
    submitDeleteUserDialog,
    showUpdateEmailDialog,
    openUpdateEmailDialog,
    dismissUpdateEmailDialog,
    submitUpdateEmailDialog,
  } = useUsers();

  const exportToCsv = useCallback(() => {
    if (!data || data.length === 0) {
      return;
    }

    const headers = ['First Name', 'Last Name', 'User Type', 'Email Address', 'Phone Number', 'Created At'];

    const sortedData = [...data].sort((a, b) => {
      const aEmpty = !a.createdAt;
      const bEmpty = !b.createdAt;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
    });

    const rows = sortedData.map((user) => [
      user.firstName.trim(),
      user.lastName.trim(),
      user.userType,
      user.emailAddress,
      user.phoneNumber
        ? `(${user.phoneNumberPrefix?.id || ''}) ${user.phoneNumberPrefix?.value || ''} ${user.phoneNumber}`
        : '',
      user.createdAt ? dayjs(user.createdAt).format('DD-MM-YY HH:mm') : '',
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row
        .map((item) => {
          const escapedItem = String(item).replace(/"/g, '""');
          if (/[",\n]/.test(escapedItem)) {
            return `"${escapedItem}"`;
          }
          return escapedItem;
        })
        .join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    link.setAttribute('download', `users_export_${formattedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data]);

  const toolbarActions = [
    {
      id: 'manage-learners',
      label: 'Manage learners',
      startIcon: <AssignLearnerIcon />,
      disabled: !(selectedUser && selectedUser.userType === 'tutor' && selectedUser.verified),
      onClick: openAssignLearnersDialog,
    },
    {
      id: 'update-email',
      label: 'Update Email',
      startIcon: <EmailIcon />,
      disabled: !(selectedUser && selectedUser.userType !== 'admin'),
      onClick: openUpdateEmailDialog,
    },
    {
      id: 'delete-user',
      label: 'Delete user',
      startIcon: <DeleteUserIcon />,
      disabled: !selectedUser,
      onClick: openDeleteUserDialog,
    },
    {
      id: 'assume-user',
      label: 'Assume user',
      startIcon: <AssumeUserIcon />,
      disabled: !(selectedUser && selectedUser.userType !== 'admin'),
      onClick: onAssumeUserClick,
    },
    {
      id: 'reset-password',
      label: 'Reset Password',
      startIcon: <LockResetIcon />,
      disabled: !(selectedUser && selectedUser.userType !== 'admin'),
      onClick: openResetPasswordDialog,
    },
  ];

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <PageTitle title="Users" />
      </div>
      <div className={classes.content}>
        <Grid container className={classes.grid}>
          <Grid item xs={12} className={classes.gridItem}>
            <Paper className={classes.background}>
              {alert && (
                <Alert severity={alert.severity} className={classes.alert}>
                  {alert.message}
                </Alert>
              )}
              <PathDataGrid
                className={classes.dataGrid}
                initialSorting={[{ id: 'firstName', desc: false }]}
                loading={loading}
                rows={data}
                columns={columns}
                checkboxSelection={false}
                getRowId={(row) => row.id}
                onRefresh={onRefreshClick}
                onExport={exportToCsv}
                toolbarActions={toolbarActions}
                showColumnFilters
                showColumnManager
                searchPlaceholder="Search..."
                onSelectionModelChange={onSelectionModelChange}
                selectionModel={selectionModel}
                pageSize={20}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>
      {showAssignLearnersDailog && (
        <AssignLearnersDialog
          id={selectedUser?.id}
          open={showAssignLearnersDailog}
          onClose={dismissAssignLearnersDialog}
          onCancel={dismissAssignLearnersDialog}
          onSubmit={submitAssignLearnersDialog}
        />
      )}
      {showPasswordResetDialog && (
        <ResetPasswordDialog
          id={selectedUser?.id}
          open={showPasswordResetDialog}
          onClose={dismissPasswordResetDialog}
          onCancel={dismissPasswordResetDialog}
          onSubmit={submitPasswordResetDialog}
        />
      )}
      {showDeleteUserDialog && (
        <DeleteUserDialog
          open={showDeleteUserDialog}
          onClose={dismissDeleteUserDialog}
          onSubmit={submitDeleteUserDialog}
        />
      )}
      {showUpdateEmailDialog && (
        <UpdateEmailDialog
          open={showUpdateEmailDialog}
          currentEmail={selectedUser?.emailAddress}
          onClose={dismissUpdateEmailDialog}
          onSubmit={submitUpdateEmailDialog}
        />
      )}
    </div>
  );
}

export default Users;
